import React from "react";
import { FieldValues } from "react-hook-form";

import {
  BaseFormInputPropsType,
  VnFormDescription,
  VnFormErrorMessage,
  VnFormField,
  VnFormItem,
  VnFormLabel,
} from "@/components/VnForm";
import { OptionType } from "@/domain/common";
import { I18nKeyType } from "@/i18n";
import { cn } from "@/utils/tailwind";

import { VnBox } from "../VnBox";
import { VnButton } from "../VnButton/VnButton";
import { VnText } from "../VnText/VnText";

type VnButtonSelectInputPropsType<T extends FieldValues> =
  BaseFormInputPropsType<T> & {
    options: OptionType[];
    columns?: 1 | 2 | 3;
    buttonSize?: "default" | "sm" | "lg";
    descriptionI18nKey?: I18nKeyType;
    descriptionI18nOptions?: object;
    buttonsClassName?: string;
  };

export const VnButtonSelectInput = <T extends FieldValues>({
  name,
  control,
  options,
  columns = 1,
  buttonSize = "default",
  isDisabled = false,
  labelI18nKey,
  labelI18nOptions,
  descriptionI18nKey,
  descriptionI18nOptions,
  showError = true,
  errorI18nKey,
  errorI18nOptions,
  className,
  buttonsClassName,
  isRequired = true,
}: VnButtonSelectInputPropsType<T>) => {
  const getContainerClassName = () =>
    columns === 1
      ? "flex flex-col gap-2"
      : "flex flex-row flex-wrap justify-between gap-y-2";

  const getButtonBasisClassName = () => {
    switch (columns) {
      case 2:
        return "basis-[49%]";
      case 3:
        return "basis-[32%]";
      default:
        return "";
    }
  };

  return (
    <VnFormField
      name={name}
      control={control}
      render={({ field }) => (
        <VnFormItem>
          <VnBox className={cn("gap-2", className)}>
            {labelI18nKey && (
              <VnFormLabel
                i18nKey={labelI18nKey}
                i18nOptions={labelI18nOptions}
                isRequired={isRequired}
              />
            )}
            {descriptionI18nKey && (
              <VnFormDescription
                i18nKey={descriptionI18nKey}
                i18nOptions={descriptionI18nOptions}
              />
            )}
            <VnBox className={getContainerClassName()}>
              {options.map(option => {
                const isSelected = field.value === option.value;

                return (
                  <VnButton
                    key={option.value}
                    variant={isSelected ? "chipSelected" : "chipUnselected"}
                    size={buttonSize}
                    isDisabled={isDisabled}
                    onPress={() => field.onChange(option.value)}
                    className={cn(getButtonBasisClassName(), buttonsClassName)}>
                    <VnText variant="lg">{option.label}</VnText>
                  </VnButton>
                );
              })}
            </VnBox>
          </VnBox>
          {showError && (
            <VnFormErrorMessage
              i18nKey={errorI18nKey}
              i18nOptions={errorI18nOptions}
            />
          )}
        </VnFormItem>
      )}
    />
  );
};
